import { useState } from "react";
import { toast } from "sonner";
import { Envelope, Phone, Lock } from "@phosphor-icons/react";
import api, { apiError } from "../lib/api";

const FIELDS = [
  { key: "email", icon: Envelope, title: "Changer d'adresse email",
    hint: "Un code de confirmation est envoyé à la nouvelle adresse. Le changement n'est effectif qu'après confirmation.",
    placeholder: "Nouvelle adresse email", type: "email" },
  { key: "phone", icon: Phone, title: "Changer de numéro de téléphone",
    hint: "Un code de confirmation est envoyé à l'adresse email actuelle du compte.",
    placeholder: "Nouveau numéro de téléphone", type: "text" },
  { key: "password", icon: Lock, title: "Changer de mot de passe",
    hint: "Votre mot de passe actuel est demandé, puis un code de confirmation est envoyé par email.",
    placeholder: "Nouveau mot de passe (6 car. min)", type: "password" },
];

function SecurityField({ field }) {
  const { key, icon: Icon, title, hint, placeholder, type } = field;
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [current, setCurrent] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setOpen(false); setValue(""); setCurrent(""); setCode(""); setSent(""); };

  const request = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/security/request", { field: key, new_value: value, current_password: current });
      setSent(data.email_masked || "votre email");
      toast.success(data.message);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/auth/security/confirm/${key}`, { code });
      toast.success(data.message);
      reset();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="px-3 py-3 rounded-2xl bg-[#F6F4EF]/60" data-testid={`security-${key}`}>
      <div className="flex items-start gap-3">
        <span className="w-8 h-8 rounded-xl bg-white flex items-center justify-center shrink-0"><Icon size={16} /></span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">{title}</p>
          <p className="text-xs text-gray-500">{hint}</p>
        </div>
        <button data-testid={`security-${key}-toggle`} onClick={() => (open ? reset() : setOpen(true))}
          className="btn-ghost py-1.5 px-3 text-xs font-semibold shrink-0">{open ? "Annuler" : "Modifier"}</button>
      </div>
      {open && (
        <div className="mt-3 space-y-2">
          {!sent ? (
            <>
              {key === "password" && (
                <input data-testid="security-current-password" type="password" className="kinput"
                  placeholder="Mot de passe actuel" value={current} onChange={(e) => setCurrent(e.target.value)} />
              )}
              <input data-testid={`security-${key}-input`} type={type} className="kinput"
                placeholder={placeholder} value={value} onChange={(e) => setValue(e.target.value)} />
              <button data-testid={`security-${key}-request-btn`} className="btn-amber w-full py-2.5"
                disabled={busy || !value || (key === "password" && !current)} onClick={request}>
                {busy ? "Envoi..." : "Recevoir le code de confirmation"}
              </button>
            </>
          ) : (
            <>
              <p className="text-xs text-gray-600">Code envoyé à {sent}. Saisissez-le pour valider le changement.</p>
              <input data-testid={`security-${key}-code-input`} className="kinput font-mono-doc"
                placeholder="Code à 6 chiffres" value={code} onChange={(e) => setCode(e.target.value)} />
              <button data-testid={`security-${key}-confirm-btn`} className="btn-amber w-full py-2.5"
                disabled={busy || !code} onClick={confirm}>{busy ? "Validation..." : "Confirmer le changement"}</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function SecuritySection() {
  return (
    <div className="kcard p-5 space-y-3" data-testid="security-section">
      <div>
        <h3 className="font-display font-bold">Sécurité</h3>
        <p className="text-xs text-gray-500">Chaque modification est confirmée par email et tracée dans le journal d'activité.</p>
      </div>
      {FIELDS.map((f) => <SecurityField key={f.key} field={f} />)}
    </div>
  );
}
